import {isAfter} from 'date-fns';
import {Pattern, match} from 'ts-pattern';

import type {
  InterestPlannedActivity,
  InterestTimelineEvent,
} from '@prompt-bridge/shared/ai-assistant-core';

import {parseDate} from 'shared/web';

type TestDriveReference = {
  id: string;
  scheduledAt: string;
};

type TestDriveTimelineEvent = {
  reference: TestDriveReference;
  timelineEvent: InterestTimelineEvent;
};

export function toUpcomingTestDrivePlannedActivities(
  timeline: readonly InterestTimelineEvent[],
  testDriveByTimelineEventId: ReadonlyMap<string, TestDriveReference>,
  evaluatedAt: string
): InterestPlannedActivity[] {
  const testDriveTimeline = timeline.flatMap((timelineEvent) => {
    const reference = testDriveByTimelineEventId.get(timelineEvent.id);

    return match(reference)
      .with(Pattern.nonNullable, (testDriveReference) => [
        {reference: testDriveReference, timelineEvent},
      ])
      .otherwise(() => []);
  });
  const latestEventByTestDriveId = testDriveTimeline.reduce<Map<string, TestDriveTimelineEvent>>(
    (latestEvents, event) => {
      const currentLatestEvent = latestEvents.get(event.reference.id);

      return match(currentLatestEvent)
        .with(Pattern.nullish, () => latestEvents.set(event.reference.id, event))
        .with(Pattern.nonNullable, (latestEvent) =>
          match(
            isAfter(
              parseDate(event.timelineEvent.occurredAt),
              parseDate(latestEvent.timelineEvent.occurredAt)
            )
          )
            .with(true, () => latestEvents.set(event.reference.id, event))
            .with(false, () => latestEvents)
            .exhaustive()
        )
        .exhaustive();
    },
    new Map()
  );

  return [...latestEventByTestDriveId.values()].flatMap((event) =>
    match({
      isFuture: isAfter(parseDate(event.reference.scheduledAt), parseDate(evaluatedAt)),
      kind: event.timelineEvent.kind,
    })
      .with({isFuture: true, kind: 'testDrivePlanned'}, () => [
        {
          id: event.reference.id,
          kind: 'testDrive' as const,
          dueAt: event.reference.scheduledAt,
          completed: false,
        },
      ])
      .otherwise(() => [])
  );
}
